import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { apiRequest, formatDate } from '../lib/utils';
import { 
  Mail, 
  Phone, 
  Building, 
  Calendar,
  MessageSquare,
  CheckCircle,
  Clock,
  Archive,
  Trash2,
  Download,
  ChevronLeft,
  Search
} from 'lucide-react';

const AdminInquiriesPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const data = await apiRequest('/admin/inquiries');
      setInquiries(data || []);
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await apiRequest(`/admin/inquiries/${id}?inquiry_status=${status}`, { method: 'PUT' });
      setInquiries(inquiries.map(i => i.id === id ? { ...i, status } : i));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const saveNotes = async (id) => {
    try {
      await apiRequest(`/admin/inquiries/${id}?notes=${encodeURIComponent(notes)}`, { method: 'PUT' });
      setInquiries(inquiries.map(i => i.id === id ? { ...i, notes } : i));
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    
    try {
      await apiRequest(`/admin/inquiries/${id}`, { method: 'DELETE' });
      setInquiries(inquiries.filter(i => i.id !== id));
      setSelectedInquiry(null);
      toast.success('Inquiry deleted');
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Company', 'Specialization', 'Message', 'Status'];
    const rows = inquiries.map(i => [
      i.created_at,
      `${i.first_name} ${i.last_name}`,
      i.email,
      i.phone || '',
      i.company || '',
      i.specialization || '',
      i.message || '',
      i.status
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inquiries-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Exported to CSV');
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesFilter = filter === 'all' || inquiry.status === filter;
    const matchesSearch = !searchTerm || 
      `${inquiry.first_name} ${inquiry.last_name} ${inquiry.email} ${inquiry.company || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <Clock className="h-4 w-4 text-blue-500" />;
      case 'contacted': return <Phone className="h-4 w-4 text-amber-500" />;
      case 'resolved': return <CheckCircle className="h-4 w-4 text-green-500" />;
      default: return <Archive className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'contacted': return 'bg-amber-100 text-amber-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-inquiries-page">
        <div className="mb-6">
          <Link to="/admin" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Contact Inquiries</h1>
              <p className="text-slate-600 mt-1">Manage incoming contact form submissions</p>
            </div>
            <Button onClick={exportToCSV} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              className="w-full pl-10 pr-4 py-2 border rounded-md"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{inquiries.filter(i => i.status === 'new').length}</p>
              <p className="text-sm text-slate-600">New</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{inquiries.filter(i => i.status === 'contacted').length}</p>
              <p className="text-sm text-slate-600">Contacted</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{inquiries.filter(i => i.status === 'resolved').length}</p>
              <p className="text-sm text-slate-600">Resolved</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Inquiries List */}
          <Card>
            <CardHeader>
              <CardTitle>Inquiries ({filteredInquiries.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto">
                {filteredInquiries.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No inquiries found</p>
                ) : (
                  filteredInquiries.map((inquiry) => (
                    <div
                      key={inquiry.id}
                      className={`p-4 border-b cursor-pointer hover:bg-slate-50 ${selectedInquiry?.id === inquiry.id ? 'bg-blue-50' : ''}`}
                      onClick={() => {
                        setSelectedInquiry(inquiry);
                        setNotes(inquiry.notes || '');
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{inquiry.first_name} {inquiry.last_name}</p>
                          <p className="text-sm text-slate-500">{inquiry.email}</p>
                          {inquiry.specialization && (
                            <p className="text-xs text-slate-400 mt-1">{inquiry.specialization}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${getStatusColor(inquiry.status)}`}>
                            {inquiry.status}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        <Calendar className="h-3 w-3 inline mr-1" />
                        {formatDate(inquiry.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Selected Inquiry Details */}
          <Card>
            <CardHeader>
              <CardTitle>Inquiry Details</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedInquiry ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-semibold">{selectedInquiry.first_name} {selectedInquiry.last_name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusIcon(selectedInquiry.status)}
                        <span className={`text-sm px-2 py-0.5 rounded ${getStatusColor(selectedInquiry.status)}`}>
                          {selectedInquiry.status}
                        </span>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600"
                      onClick={() => deleteInquiry(selectedInquiry.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <a href={`mailto:${selectedInquiry.email}`} className="text-blue-600 hover:underline">
                        {selectedInquiry.email}
                      </a>
                    </p>
                    {selectedInquiry.phone && (
                      <p className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-slate-400" />
                        <a href={`tel:${selectedInquiry.phone}`} className="text-blue-600 hover:underline">
                          {selectedInquiry.phone}
                        </a>
                      </p>
                    )}
                    {selectedInquiry.company && (
                      <p className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-slate-400" />
                        {selectedInquiry.company}
                      </p>
                    )}
                    {selectedInquiry.specialization && (
                      <p className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-slate-400" />
                        {selectedInquiry.specialization}
                      </p>
                    )}
                  </div>

                  {selectedInquiry.message && (
                    <div className="bg-slate-50 p-3 rounded-md">
                      <p className="text-sm font-medium mb-1">Message:</p>
                      <p className="text-sm text-slate-600">{selectedInquiry.message}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium mb-1 block">Update Status:</label>
                    <Select 
                      value={selectedInquiry.status} 
                      onValueChange={(value) => updateInquiryStatus(selectedInquiry.id, value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Internal Notes:</label>
                    <textarea
                      className="w-full border rounded-md p-2 min-h-[80px] text-sm"
                      placeholder="Add notes about this inquiry..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                    <Button 
                      size="sm" 
                      className="mt-2 bg-[#1a365d]"
                      onClick={() => saveNotes(selectedInquiry.id)}
                    >
                      Save Notes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Select an inquiry to view details</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminInquiriesPage;
